import { createI18n } from 'vue-i18n'

import { DEFAULT_LOCALE, LOCALES, Locale } from '@/enums/locale'

import en from './locales/en'
import sl from './locales/sl'

export type AvailableLocales = 'sl' | 'en'

/** Whether a value is one of the console's languages. */
export const isLocale = (value: unknown): value is Locale => LOCALES.includes(value as Locale)

/*
 * How many of a thing there are, in a language that does not count the way English does.
 *
 * Vue I18n picks a plural form by index and its built-in rule knows one and other. Slovenian
 * has four forms (1 naloga, 2 nalogi, 3 naloge, 5 nalog); the rule is CLDR's, and the index is
 * clamped to the forms a message actually offers, so a message written with fewer still renders.
 */
const pick = (index: number, choices: number): number => Math.min(index, choices - 1)

const slovenian = (choice: number, choices: number): number => {
    const hundreds = Math.abs(choice) % 100

    if (hundreds === 1)                   return pick(0, choices)
    if (hundreds === 2)                   return pick(1, choices)
    if (hundreds === 3 || hundreds === 4) return pick(2, choices)

    return pick(3, choices)
}

const stored = localStorage.getItem('locale')

/*
 * Slovenian first: the people who use this console work in it. English is the fallback, so
 * a key that exists in only one file still renders rather than showing its own name — and the
 * two files are checked against one schema (`MessageSchema` in `sl.ts`: `en.ts` is
 * `satisfies MessageSchema`), so a key missing from either fails the type-check. The
 * language chosen before signing in is remembered per browser under `locale`; after signing
 * in the person's own (`Me.locale`) takes over (`stores/app.ts`).
 */
const i18n = createI18n({
    legacy:           false,
    globalInjection:  true,
    locale:           isLocale(stored) ? stored : DEFAULT_LOCALE,
    fallbackLocale:   'en',
    messages:         { sl, en },
    availableLocales: ['sl', 'en'],
    pluralRules:      { sl: slovenian },
})

export default i18n
